import React, {useState} from 'react';

import type {useHoverPopover} from 'lib/hooks/useHoverPopover';

import AnnotationEditor from './AnnotationEditor';
import {useAnnotationModeStore} from './AnnotationModeStore';
import AnnotationNoteList from './AnnotationNoteList';
import {isSupplementAnchor} from './annotationAnchors';
import type {AnnotationNoteLine} from './annotationNoteLines';
import {selectIsNoteListed} from './annotationSelectors';
import {useIsAnnotationModeAvailable} from './useIsAnnotationModeAvailable';
import {useRegionEdit} from './useRegionEdit';

/** The note being edited; its editor hangs from the peek itself. */
interface PeekEdit {
  id: string;
  regionId: string;
  /** A whole-supplement mark paints as a border, which an underline cannot express. */
  allowUnderline: boolean;
}

interface AnnotationNotePeekProps {
  line: AnnotationNoteLine;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  contentProps: ReturnType<typeof useHoverPopover>['contentProps'];
}

/**
 * One line's notes, each editable in place. Mounted only while the peek is open,
 * so closing it discards the open editor rather than springing it on the next hover.
 */
export default function AnnotationNotePeek({
  line,
  triggerRef,
  contentProps
}: AnnotationNotePeekProps) {
  const [editingNote, openEditor, closeEditor] = useRegionEdit<PeekEdit>();
  const canEditAnnotations = useIsAnnotationModeAvailable();
  // State, not a ref: Popover reads `targetRef.current` during render.
  const [peekEl, setPeekEl] = useState<HTMLDivElement | null>(null);
  const isEditedNoteListed = useAnnotationModeStore((state) =>
    selectIsNoteListed(state, editingNote?.regionId, editingNote?.id)
  );
  const activeEdit = isEditedNoteListed ? editingNote : null;

  return (
    <div
      {...contentProps}
      ref={setPeekEl}
      className='p-x1-half max-h-[320px] w-[260px] overflow-y-auto'
      data-testid='annotation-note-popover'
      onKeyDown={(event) => {
        // Unprevented, an Escape from a pencil would exit annotation mode.
        if (event.key !== 'Escape' || activeEdit) {
          return;
        }
        event.preventDefault();
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }}
    >
      <AnnotationNoteList
        annotations={line.annotations}
        editingNoteId={activeEdit?.id}
        onEditNote={
          canEditAnnotations
            ? ({id, anchor}) =>
                openEditor(
                  {id, regionId: anchor.regionId, allowUnderline: !isSupplementAnchor(anchor)},
                  anchor.regionId
                )
            : undefined
        }
      />
      {/* Inside the content, unlike the panel's: React routes enter/leave and
          focus through its own tree, so the hover bridge holds the peek open. */}
      {activeEdit && (
        <AnnotationEditor
          regionId={activeEdit.regionId}
          activeAnnotation={{id: activeEdit.id}}
          anchorEl={peekEl}
          allowUnderline={activeEdit.allowUnderline}
          onClose={() => {
            // Outlives the pencil, which Remove takes away.
            triggerRef.current?.focus({preventScroll: true});
            closeEditor();
          }}
        />
      )}
    </div>
  );
}
